import React, { useState, useEffect } from 'react';
import { Sparkles, ChevronRight, Lock, Trophy } from 'lucide-react';
import { apiClient } from '../../api/client';

const STAGE_ICONS = {
  seedling: (
    <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" stroke="currentColor" strokeWidth="2">
      <path d="M12 22V12M12 12C12 7 7 2 2 2c0 5 5 10 10 10zM12 12c0-5 5-10 10-10 0 5-5 10-10 10z"/>
    </svg>
  ),
  sprout: (
    <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" stroke="currentColor" strokeWidth="2">
      <path d="M12 22V8M12 8C12 4 8 1 4 1c0 4 4 7 8 7zM12 8c0-4 4-7 8-7-0 4-4 7-8 7zM8 22h8"/>
    </svg>
  ),
  flower: (
    <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="10" r="3"/><path d="M12 7V2M15 10l4-3M15 13l4 3M12 16v5M9 13l-4 3M9 10l-4-3"/>
    </svg>
  ),
  star: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
    </svg>
  ),
  crown: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
      <path d="M2 20h20v2H2zM4 18l2-10 4 4 2-8 2 8 4-4 2 10H4z"/>
    </svg>
  ),
};

const AvAEvolutionCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    apiClient.request('/api/enhancements/ava-evolution')
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div data-testid="ava-evolution-card" className="bg-white rounded-2xl border border-gray-100 p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-48 mb-4" />
        <div className="h-4 bg-gray-200 rounded w-full mb-2" />
        <div className="h-20 bg-gray-100 rounded" />
      </div>
    );
  }

  if (!data) return null;

  const { current_stage, next_stage, progress_percent, conversations_to_next, total_conversations, all_stages, newly_unlocked } = data;

  return (
    <div data-testid="ava-evolution-card" className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      {/* Header with current stage */}
      <div className="p-6 pb-4" style={{ background: `linear-gradient(135deg, ${current_stage.color}15, ${current_stage.color}08)` }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${current_stage.color}20`, color: current_stage.color }}>
              {STAGE_ICONS[current_stage.icon] || <Sparkles className="w-6 h-6" />}
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">{current_stage.name}</h3>
              <p className="text-sm text-gray-500">Stage {current_stage.stage} of 5</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold" style={{ color: current_stage.color }}>{total_conversations}</p>
            <p className="text-xs text-gray-400">conversations</p>
          </div>
        </div>

        <p className="text-sm text-gray-600 mb-4">{current_stage.description}</p>

        {/* Progress bar */}
        {next_stage && (
          <div>
            <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
              <span>{progress_percent}% to {next_stage.name}</span>
              <span>{conversations_to_next} more conversations</span>
            </div>
            <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-1000 ease-out"
                style={{ width: `${progress_percent}%`, background: `linear-gradient(90deg, ${current_stage.color}, ${next_stage.color})` }}
              />
            </div>
          </div>
        )}
        {!next_stage && (
          <div className="flex items-center gap-2 text-sm font-medium" style={{ color: current_stage.color }}>
            <Trophy className="w-4 h-4" />
            Maximum evolution reached!
          </div>
        )}
      </div>

      {/* Newly unlocked alert */}
      {newly_unlocked && newly_unlocked.length > 0 && (
        <div className="mx-6 mt-2 p-3 rounded-xl bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200">
          <p className="text-sm font-semibold text-amber-800 flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            New stage unlocked! NFT collectible earned.
          </p>
        </div>
      )}

      {/* Evolution timeline */}
      <button
        data-testid="toggle-evolution-stages"
        onClick={() => setShowAll(!showAll)}
        className="w-full px-6 py-3 flex items-center justify-between text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
      >
        <span>Evolution Journey</span>
        <ChevronRight className={`w-4 h-4 transition-transform ${showAll ? 'rotate-90' : ''}`} />
      </button>

      {showAll && (
        <div className="px-6 pb-5 space-y-3">
          {all_stages.map((stage, i) => {
            const isReached = total_conversations >= stage.threshold;
            const isCurrent = stage.stage === current_stage.stage;
            return (
              <div key={stage.stage} className={`flex items-center gap-3 p-2 rounded-lg ${isCurrent ? 'bg-gray-50 ring-1 ring-gray-200' : ''}`}>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm ${isReached ? '' : 'opacity-40'}`}
                  style={{ background: isReached ? `${stage.color}20` : '#f3f4f6', color: isReached ? stage.color : '#9ca3af' }}>
                  {isReached ? (STAGE_ICONS[stage.icon] || <Sparkles className="w-4 h-4" />) : <Lock className="w-3 h-3" />}
                </div>
                <div className="flex-1">
                  <p className={`text-sm font-medium ${isReached ? 'text-gray-900' : 'text-gray-400'}`}>{stage.name}</p>
                  <p className="text-xs text-gray-400">{stage.threshold}+ conversations</p>
                </div>
                {isCurrent && <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: `${stage.color}20`, color: stage.color }}>Current</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AvAEvolutionCard;
